import { useRef, useEffect } from 'react';
import { useEditorStore } from '../../../store/editorStore';

function hexToRgba(hex, opacity) {
  if (!hex?.startsWith('#')) return hex || 'transparent';
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity})`;
}

export default function TextLayerRenderer({ layer, isEditing }) {
  const data = layer.data;
  const rawStyle = data.style || {};
  const style = {
    ...rawStyle,
    padding: rawStyle.padding || [0, 0, 0, 0],
    borderRadius: rawStyle.borderRadius ?? 0,
    backgroundOpacity: rawStyle.backgroundOpacity ?? 1,
  };
  const updateLayerData = useEditorStore((s) => s.updateLayerData);
  const setEditingLayer = useEditorStore((s) => s.setEditingLayer);
  const editableRef = useRef(null);

  useEffect(() => {
    if (isEditing && editableRef.current) {
      editableRef.current.focus();
      // Place cursor at end
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [isEditing]);

  const containerStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: style.textAlign === 'left' ? 'flex-start' : style.textAlign === 'right' ? 'flex-end' : 'center',
  };

  const bgStyle = style.backgroundColor
    ? {
        background: hexToRgba(style.backgroundColor, style.backgroundOpacity),
        padding: `${style.padding[0]}px ${style.padding[1]}px ${style.padding[2]}px ${style.padding[3]}px`,
        borderRadius: style.borderRadius,
      }
    : {
        padding: `${style.padding[0]}px ${style.padding[1]}px ${style.padding[2]}px ${style.padding[3]}px`,
      };

  const textStyle = {
    fontFamily: `'${style.fontFamily}', sans-serif`,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    lineHeight: style.lineHeight,
    letterSpacing: style.letterSpacing,
    textAlign: style.textAlign,
    textTransform: style.textTransform,
    color: style.color,
    textShadow: style.textShadow ?? undefined,
    WebkitTextStroke: style.stroke ? `${style.strokeWidth}px ${style.stroke}` : undefined,
    margin: 0,
    padding: 0,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    width: '100%',
  };

  if (isEditing) {
    return (
      <div style={containerStyle}>
        <div style={bgStyle}>
          <div
            ref={editableRef}
            contentEditable
            suppressContentEditableWarning
            style={textStyle}
            onBlur={(e) => {
              updateLayerData(layer.id, { content: e.currentTarget.innerText });
              setEditingLayer(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setEditingLayer(null);
              }
            }}
            dangerouslySetInnerHTML={{ __html: data.content.replace(/\n/g, '<br>') }}
          />
        </div>
      </div>
    );
  }

  const displayContent = style.textTransform === 'capitalize'
    ? data.content.toLowerCase()
    : data.content;

  return (
    <div style={containerStyle}>
      <div style={bgStyle}>
        <p style={textStyle}>
          {displayContent.split('\n').map((line, i, arr) => (
            <span key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </span>
          ))}
        </p>
      </div>
    </div>
  );
}
